import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { Protocol } from 'pmtiles';
import { WeatherLayerManager } from './WeatherLayerManager.js';

// Register PMTiles protocol once globally
const pmtilesProtocol = new Protocol();
maplibregl.addProtocol('pmtiles', pmtilesProtocol.tile);

function attachWeatherManager(map, opts) {
    if (!map._weatherManager) {
        map._weatherManager = new WeatherLayerManager(map, opts);
    }
}

const loadWindBarbImages = async (map) => {
    for (let i = 0; i <= 21; i++) {
        const paddedIndex = String(i).padStart(2, '0');
        const imageId = `wind-barb-${paddedIndex}`;
        if (!map.hasImage(imageId)) {
            const image = await map.loadImage(`${window.location.origin}/geojsons/1920px-Symbol_wind_speed_${paddedIndex}.svg.png`);
            map.addImage(imageId, image.data);
        }
    }
};

export function singleMapInitialize(containerId, mapRef, popupRef, countyState) {
    if (!mapRef.current) {
        const map = new maplibregl.Map({
            container: containerId,
            attributionControl: false,
            style: {
                "version": 8,
                "sources": {
                    "osm-tiles": {
                        "type": "raster",
                        "tiles": [
                            'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'
                        ],
                        "tileSize": 256
                    },
                    "labels": {
                        "type": "raster",
                        "tiles": [
                            'https://a.basemaps.cartocdn.com/rastertiles/voyager_only_labels/{z}/{x}/{y}{r}.png',
                            'https://b.basemaps.cartocdn.com/rastertiles/voyager_only_labels/{z}/{x}/{y}{r}.png',
                            'https://c.basemaps.cartocdn.com/rastertiles/voyager_only_labels/{z}/{x}/{y}{r}.png',
                            'https://d.basemaps.cartocdn.com/rastertiles/voyager_only_labels/{z}/{x}/{y}{r}.png',
                        ],
                        "tileSize": 256
                    }
                },
                "layers": [
                    {
                        "id": "osm-layer",
                        "type": "raster",
                        "source": "osm-tiles",
                        "paint": {
                            "raster-opacity": 1
                        }
                    },
                    {
                        "id": "labels-layer",
                        "type": "raster",
                        "source": "labels",
                        "paint": {
                            "raster-opacity": 1
                        }
                    }
                ]
            },
            center: [-90, 40],
            zoom: 3,
            minZoom: 2,
            maxZoom: 8
        });

        const nav = new maplibregl.NavigationControl({
            showCompass: false,
            showZoom: true
        });

        map.on('style.load', () => {
            map.getCanvas().style.cursor = 'pointer';
            map.addControl(nav, 'top-left');
            addBorderLayers(map, countyState)
            loadWindBarbImages(map)
            // Use a small timeout to ensure the DOM is ready
            setTimeout(() => {
                const navControl = document.querySelector('.maplibregl-ctrl.maplibregl-ctrl-group');
                if (navControl && popupRef.current) {
                    navControl.addEventListener('mouseenter', () => {
                        try {
                            popupRef.current._content.style.visibility = 'hidden'; // Hide the popup
                        }
                        catch {}
                    });

                    navControl.addEventListener('mouseleave', () => {
                        try {
                            popupRef.current._content.style.visibility = 'visible'; // Show the popup
                        }
                        catch {}
                    });
                }
            }, 100); // Small delay to ensure DOM is ready
        });

        map.on('mousedown', () => {
            map.getCanvas().style.cursor = '';
        });

        map.on('mouseup', () => {
            map.getCanvas().style.cursor = 'pointer';
        });

        attachWeatherManager(map);
        mapRef.current = map; // Store the map instance in mapRef
    }

    // Cleanup function to remove the single map
    return () => {
        if (mapRef.current) {
            mapRef.current.remove();
            mapRef.current = null; // Clear the reference
        }
    };
}

export function griddedMapsInitialize(containerIds, mapRefs, popupRef, countyState) {
    containerIds.forEach((containerId, index) => {
      if (!mapRefs.current[index]) {
        const map = new maplibregl.Map({
          container: containerId,
          attributionControl: false,
          style: {
            version: 8,
            sources: {
              "osm-tiles": {
                type: "raster",
                tiles: [
                  'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
                ],
                tileSize: 256,
              },
              labels: {
                type: "raster",
                tiles: [
                  'https://a.basemaps.cartocdn.com/rastertiles/voyager_only_labels/{z}/{x}/{y}{r}.png',
                  'https://b.basemaps.cartocdn.com/rastertiles/voyager_only_labels/{z}/{x}/{y}{r}.png',
                  'https://c.basemaps.cartocdn.com/rastertiles/voyager_only_labels/{z}/{x}/{y}{r}.png',
                  'https://d.basemaps.cartocdn.com/rastertiles/voyager_only_labels/{z}/{x}/{y}{r}.png',
                ],
                tileSize: 256,
              },
            },
            layers: [
              {
                id: "osm-layer",
                type: "raster",
                source: "osm-tiles",
                paint: {
                  "raster-opacity": 1,
                },
              },
              {
                id: "labels-layer",
                type: "raster",
                source: "labels",
                paint: {
                  "raster-opacity": 1,
                },
              },
            ],
          },
          center: [-95, 40],
          zoom: 2,
          minZoom: 2,
          maxZoom: 8,
        });
  
        const nav = new maplibregl.NavigationControl({
          showCompass: false,
          showZoom: true,
        });
  
        map.on("style.load", () => {
          map.getCanvas().style.cursor = "pointer";
          map.addControl(nav, "bottom-left");
          addBorderLayers(map, countyState)
          loadWindBarbImages(map)
            // Use a small timeout to ensure the DOM is ready
            setTimeout(() => {
                const navControl = document.querySelector('.maplibregl-ctrl.maplibregl-ctrl-group');
                if (navControl && popupRef.current) {
                    navControl.addEventListener('mouseenter', () => {
                        if (popupRef.current._content) {
                            popupRef.current._content.style.visibility = 'hidden'; // Hide the popup
                        }
                    });

                    navControl.addEventListener('mouseleave', () => {
                        if (popupRef.current._content) {
                            popupRef.current._content.style.visibility = 'visible'; // Show the popup
                        }
                    });
                }
            }, 100); // Small delay to ensure DOM is ready
        });
  
        map.on("mousedown", () => {
          map.getCanvas().style.cursor = "";
        });
  
        map.on("mouseup", () => {
          map.getCanvas().style.cursor = "pointer";
        });
  
        attachWeatherManager(map, { prefetchAhead: 1 });
        mapRefs.current[index] = map; // Store the map instance in the mapRefs array
      }
    });
  
    // Cleanup function to remove all maps
    return () => {
      mapRefs.current.forEach((map) => {
        if (map) {
          map.remove();
        }
      });
      mapRefs.current = []; // Clear the references
    };
  }

export function removeSingleMap(singleMapRef) {
    if (singleMapRef.current) {
        singleMapRef.current.remove();
        singleMapRef.current = null; // Clear the reference
    }
}

export function removeGriddedMaps(griddedMapRefs) {
    griddedMapRefs.current.forEach((map, index) => {
        if (map) {
            map.remove();
            griddedMapRefs.current[index] = null; // Clear each map reference
        }
    });
}

function addBorderLayers(map, countyState) {
    if (!map.getSource('countries')) {
        map.addSource('countries', {
            type: 'vector',
            tiles: [
				`${window.location.origin}/geojsons/countries/{z}/{x}/{y}.pbf`
            ],
            minzoom: 2,
            maxzoom: 8
        });
    }
    map.addLayer({
        'id': 'countries-layer',
        'type': 'line',
        'source': 'countries',
        'source-layer': 'countries',
        'paint': {
            'line-color': 'black',
            'line-width': 0.75
        }
    },'labels-layer');

    if (!map.getSource('us-states')) {
        map.addSource('us-states', {
            type: 'vector',
            tiles: [
                `${window.location.origin}/geojsons/states/{z}/{x}/{y}.pbf`
            ],
            minzoom: 2,
            maxzoom: 8
        });
    }

    map.addLayer({
        'id': 'states-layer',
        'type': 'line',
        'source': 'us-states',
        'source-layer': 'usstates_wrap',
        'paint': {
            'line-color': 'black',
            'line-width': 0.75
        }
    },'labels-layer');

    if (countyState) {
        if (!map.getSource('us-counties')) {
            map.addSource('us-counties', {
                type: 'vector',
                tiles: [
                    `${window.location.origin}/geojsons/counties/{z}/{x}/{y}.pbf`
                ],
                minzoom: 2,
                maxzoom: 8
            });
        }

        map.addLayer({
            'id': 'counties-layer',
            'type': 'line',
            'source': 'us-counties',
            'source-layer': 'counties',
            'paint': {
                'line-color': 'black',
                'line-width': 0.10
            }
        },'labels-layer');
    }
};
